import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { ApiError } from '../api/client';
import type { LoginResultDto } from '../api/types';
import { Field, FormErrors, Spinner } from '../components/ui';
import { Seo } from '../components/Seo';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

/* ------------------------------------------------------------------ *
 *  Kimlik ekranlari: giris, iki adimli dogrulama, kayit, parola
 *  sifirlama ve e-posta onayi.
 * ------------------------------------------------------------------ */

function AuthShell({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="container-page flex justify-center py-12">
      <Seo title={title} description={description} noIndex />
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {description ? <p className="mt-2 text-sm text-ink-600">{description}</p> : null}
        <div className="card mt-6 p-6">{children}</div>
        {footer ? <div className="mt-5 text-center text-sm text-ink-600">{footer}</div> : null}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, completeTwoFactor, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get('devam') ?? params.get('redirect') ?? '/hesabim';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [pending, setPending] = useState<LoginResultDto | null>(null);

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const mutation = useMutation({
    mutationFn: () => login({ email, password, rememberMe }),
    onSuccess: (result) => {
      if (result.requiresTwoFactor) {
        setPending(result);
        return;
      }
      navigate(redirectTo, { replace: true });
    },
  });

  if (pending?.twoFactorToken) {
    return (
      <TwoFactorStep
        result={pending}
        rememberMe={rememberMe}
        onVerified={async () => {
          await completeTwoFactor(pending);
          navigate(redirectTo, { replace: true });
        }}
        onCancel={() => setPending(null)}
      />
    );
  }

  const error = mutation.error;
  const locked = error instanceof ApiError && error.isLocked;

  return (
    <AuthShell
      title="Giriş yap"
      description="Siparişlerinizi takip etmek ve daha hızlı alışveriş yapmak için giriş yapın."
      footer={
        <>
          Hesabınız yok mu?{' '}
          <Link to="/kayit" className="link">
            Üye olun
          </Link>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        {locked ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" role="alert">
            <p className="text-sm font-medium text-amber-900">Hesabınız geçici olarak kilitli</p>
            <p className="mt-1 text-sm text-amber-800">{error.errors[0]}</p>
          </div>
        ) : (
          <FormErrors error={error} />
        )}

        <Field label="E-posta" htmlFor="login-email" required>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            className="field"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Parola" htmlFor="login-password" required>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            className="field"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
            />
            Beni hatırla
          </label>
          <Link to="/parola-sifirla" className="text-sm text-brand-700 hover:underline">
            Parolamı unuttum
          </Link>
        </div>

        <button type="submit" className="btn-primary w-full" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
          Giriş yap
        </button>

        <p className="text-xs text-ink-500">
          Üç kez hatalı giriş yapılırsa hesap 15 dakika süreyle kilitlenir.
        </p>
      </form>
    </AuthShell>
  );
}

function TwoFactorStep({
  result,
  rememberMe,
  onVerified,
  onCancel,
}: {
  result: LoginResultDto;
  rememberMe: boolean;
  onVerified: () => Promise<void>;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [code, setCode] = useState('');

  const verify = useMutation({
    mutationFn: () =>
      authApi.verifyTwoFactor({
        twoFactorToken: result.twoFactorToken!,
        code,
        rememberMe,
      }),
    onSuccess: () => void onVerified(),
  });

  const resend = useMutation({
    mutationFn: () => authApi.resendTwoFactorCode({ twoFactorToken: result.twoFactorToken! }),
    onSuccess: (message) => toast.success(message ?? 'Kod yeniden gönderildi.'),
    onError: (error) => toast.fromError(error, 'Kod gönderilemedi.'),
  });

  return (
    <AuthShell
      title="Doğrulama kodu"
      description={`Kodu ${result.maskedEmail ?? 'e-posta adresinize'} gönderdik.`}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          verify.mutate();
        }}
      >
        <FormErrors error={verify.error} />

        <Field label="6 haneli kod" htmlFor="tfa-code" required>
          <input
            id="tfa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            className="field text-center text-lg tracking-[0.4em]"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
        </Field>

        <button type="submit" className="btn-primary w-full" disabled={verify.isPending}>
          {verify.isPending ? <Spinner className="h-4 w-4" /> : null}
          Doğrula
        </button>

        <div className="flex items-center justify-between text-sm">
          <button type="button" className="link" onClick={() => resend.mutate()} disabled={resend.isPending}>
            Kodu yeniden gönder
          </button>
          <button type="button" className="text-ink-500 hover:text-ink-800" onClick={onCancel}>
            Vazgeç
          </button>
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    acceptsMarketing: false,
    acceptsTerms: false,
  });

  const mutation = useMutation({
    mutationFn: () => authApi.register(form),
    onSuccess: (message) => {
      toast.success(message ?? 'Kaydınız alındı.', 'E-posta adresinize doğrulama bağlantısı gönderildi.');
      navigate('/giris');
    },
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <AuthShell
      title="Üye ol"
      description="Siparişlerinizi takip edin, favorilerinizi kaydedin."
      footer={
        <>
          Zaten üye misiniz?{' '}
          <Link to="/giris" className="link">
            Giriş yapın
          </Link>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <FormErrors error={mutation.error} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Ad" htmlFor="reg-first" required>
            <input
              id="reg-first"
              className="field"
              required
              value={form.firstName}
              onChange={(event) => set('firstName', event.target.value)}
            />
          </Field>
          <Field label="Soyad" htmlFor="reg-last" required>
            <input
              id="reg-last"
              className="field"
              required
              value={form.lastName}
              onChange={(event) => set('lastName', event.target.value)}
            />
          </Field>
        </div>

        <Field label="E-posta" htmlFor="reg-email" required>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            required
            className="field"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
          />
        </Field>

        <Field label="Telefon" htmlFor="reg-phone">
          <input
            id="reg-phone"
            type="tel"
            className="field"
            value={form.phoneNumber}
            onChange={(event) => set('phoneNumber', event.target.value)}
          />
        </Field>

        <Field
          label="Parola"
          htmlFor="reg-password"
          required
          hint="En az 10 karakter; büyük/küçük harf, rakam ve sembol içermeli."
        >
          <input
            id="reg-password"
            type="password"
            autoComplete="new-password"
            required
            className="field"
            value={form.password}
            onChange={(event) => set('password', event.target.value)}
          />
        </Field>

        <Field label="Parola tekrar" htmlFor="reg-confirm" required>
          <input
            id="reg-confirm"
            type="password"
            autoComplete="new-password"
            required
            className="field"
            value={form.confirmPassword}
            onChange={(event) => set('confirmPassword', event.target.value)}
          />
        </Field>

        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            required
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={form.acceptsTerms}
            onChange={(event) => set('acceptsTerms', event.target.checked)}
          />
          <span>
            <Link to="/sayfa/uyelik-sozlesmesi" className="link" target="_blank">
              Üyelik sözleşmesi
            </Link>
            'ni okudum ve kabul ediyorum.
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={form.acceptsMarketing}
            onChange={(event) => set('acceptsMarketing', event.target.checked)}
          />
          Kampanya ve yeniliklerden e-posta ile haberdar olmak istiyorum.
        </label>

        <button type="submit" className="btn-primary w-full" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
          Hesap oluştur
        </button>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  // E-postadaki linkten token ile gelindiyse doğrudan parola belirleme ekranı gösterilir
  if (token) {
    return <ResetPasswordPage />;
  }

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () => authApi.forgotPassword({ email }),
    onSuccess: () => setSent(true),
  });

  return (
    <AuthShell
      title="Parolamı unuttum"
      description="Kayıtlı e-posta adresinize sıfırlama bağlantısı gönderelim."
      footer={
        <Link to="/giris" className="link">
          Giriş ekranına dön
        </Link>
      }
    >
      {sent ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Bu adres kayıtlıysa sıfırlama bağlantısı gönderildi. Gelen kutunuzu kontrol edin.
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <FormErrors error={mutation.error} />

          <Field label="E-posta" htmlFor="forgot-email" required>
            <input
              id="forgot-email"
              type="email"
              required
              className="field"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <button type="submit" className="btn-primary w-full" disabled={mutation.isPending}>
            {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
            Bağlantı gönder
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const tokenParam = params.get('token') ?? '';
  const emailParam = params.get('email') ?? '';

  const [form, setForm] = useState({
    email: emailParam,
    token: tokenParam,
    newPassword: '',
    confirmNewPassword: '',
  });

  useEffect(() => {
    if (emailParam && !form.email) {
      setForm((c) => ({ ...c, email: emailParam }));
    }
    if (tokenParam && !form.token) {
      setForm((c) => ({ ...c, token: tokenParam }));
    }
  }, [emailParam, tokenParam, form.email, form.token]);

  const mutation = useMutation({
    mutationFn: () => authApi.resetPassword(form),
    onSuccess: (message) => {
      toast.success(message ?? 'Parolanız başarıyla güncellendi.');
      navigate('/giris');
    },
  });

  return (
    <AuthShell title="Yeni parola belirle" description="Güvenliğiniz için yeni parolanızı belirleyin.">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (form.newPassword !== form.confirmNewPassword) {
            toast.error('Girdiğiniz parolalar birbiriyle eşleşmiyor.');
            return;
          }
          mutation.mutate();
        }}
      >
        <FormErrors error={mutation.error} />

        {form.email && tokenParam ? (
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5 text-xs text-brand-900">
            <strong>{form.email}</strong> hesabı için yeni parola belirliyorsunuz.
          </div>
        ) : null}

        {!tokenParam ? (
          <>
            <Field label="E-posta" htmlFor="reset-email" required>
              <input
                id="reset-email"
                type="email"
                required
                className="field"
                value={form.email}
                onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
              />
            </Field>
            <Field label="Sıfırlama kodu" htmlFor="reset-token" required>
              <input
                id="reset-token"
                required
                className="field"
                value={form.token}
                onChange={(event) => setForm((c) => ({ ...c, token: event.target.value }))}
              />
            </Field>
          </>
        ) : null}

        <Field
          label="Yeni parola"
          htmlFor="reset-password"
          required
          hint="En az 6 karakter olmalı; harf ve rakam içermelidir."
        >
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            required
            className="field"
            value={form.newPassword}
            onChange={(event) => setForm((c) => ({ ...c, newPassword: event.target.value }))}
          />
        </Field>

        <Field label="Yeni parola tekrar" htmlFor="reset-confirm" required>
          <input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            required
            className="field"
            value={form.confirmNewPassword}
            onChange={(event) => setForm((c) => ({ ...c, confirmNewPassword: event.target.value }))}
          />
        </Field>

        <button type="submit" className="btn-primary w-full" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
          Parolayı güncelle
        </button>

        <div className="text-center">
          <Link to="/giris" className="text-sm text-ink-600 hover:text-ink-900">
            Giriş ekranına dön
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const token = params.get('token') ?? '';
  const [resendEmail, setResendEmail] = useState(email);

  const confirm = useMutation({
    mutationFn: () => authApi.confirmEmail({ email, token }),
  });

  const resend = useMutation({
    mutationFn: () => authApi.resendConfirmation({ email: resendEmail }),
  });

  // Baglantiyla gelindiyse dogrulama kendiliginden calisir.
  useEffect(() => {
    if (email && token) confirm.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);

  return (
    <AuthShell title="E-posta doğrulama">
      {confirm.isPending ? (
        <p className="text-sm text-ink-600">Doğrulanıyor…</p>
      ) : confirm.isSuccess ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {confirm.data ?? 'E-posta adresiniz doğrulandı.'}
          </div>
          <Link to="/giris" className="btn-primary w-full">
            Giriş yap
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <FormErrors error={confirm.error} />

          <p className="text-sm text-ink-600">
            Doğrulama bağlantısı geçersiz veya süresi dolmuş olabilir. Yeni bağlantı isteyebilirsiniz.
          </p>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              resend.mutate();
            }}
          >
            <Field label="E-posta" htmlFor="confirm-email" required>
              <input
                id="confirm-email"
                type="email"
                required
                className="field"
                value={resendEmail}
                onChange={(event) => setResendEmail(event.target.value)}
              />
            </Field>

            <button type="submit" className="btn-secondary w-full" disabled={resend.isPending}>
              {resend.isPending ? <Spinner className="h-4 w-4" /> : null}
              Doğrulama bağlantısını tekrar gönder
            </button>

            {resend.isSuccess ? (
              <p className="text-sm text-emerald-700">{resend.data ?? 'Bağlantı gönderildi.'}</p>
            ) : null}
          </form>
        </div>
      )}
    </AuthShell>
  );
}
